import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { SignUpButton, TalkToFoundersButton } from '../CTAButtons';
import { RH_MEDIA } from '../../lib/rhapsodyMedia';
import rhapsodyLogo from '../../assets/rhapsody_icon.svg';

const WORDMARK = 'Rhapsody';
const REEL = RH_MEDIA.heroReel;
const SHOT_MS = 5200;

const EASE = [0.16, 1, 0.3, 1] as const;

/* Tagline / sub / CTAs fade + rise, lightly staggered after the wordmark. */
const restItem = {
  hidden: { opacity: 0, y: 26 },
  show: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE, delay },
  }),
};

export function RhapsodyHero() {
  const [active, setActive] = useState(0);
  const reduced = useRef(false);

  // Cross-fade the reel on a timer (paused when the tab is hidden).
  useEffect(() => {
    reduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let timer = 0;
    const tick = () => {
      timer = window.setTimeout(() => {
        if (!document.hidden) setActive((n) => (n + 1) % REEL.length);
        tick();
      }, SHOT_MS);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, []);


  return (
    <header className="rh-hero">
      {/* full-bleed cinematic reel */}
      <div className="rh-hero-reel" aria-hidden="true">
        {REEL.map((s, i) => (
          <div className={`rh-reel-slide ${i === active ? 'on' : ''}`} key={s.key}>
            <video
              className="rh-reel-video"
              poster={s.poster}
              autoPlay
              muted
              loop
              playsInline
              preload={i === 0 ? 'auto' : 'metadata'}
            >
              <source src={s.video} type="video/mp4" />
            </video>
          </div>
        ))}
        <div className="rh-hero-scrim" />
        {/* <div className="rh-hero-grid" /> */}
      </div>

      <motion.h1
        className="rh-wordmark"
        aria-label={WORDMARK}
        initial={{ opacity: 0, y: 26, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1.1, ease: EASE }}
      >
        <img className="rh-wordmark-logo" src={rhapsodyLogo} alt={WORDMARK} />
      </motion.h1>

      <div className="rh-underline" aria-hidden="true">
        <svg viewBox="0 0 560 20" fill="none">
          <motion.path
            d="M4 14 C 120 4, 240 18, 340 10 S 520 6, 556 12"
            stroke="var(--color-primary)"
            strokeWidth="2"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.1, ease: 'easeInOut', delay: 0.45 }}
          />
        </svg>
      </div>

      <motion.p
        className="rh-hero-tagline"
        variants={restItem}
        custom={0.5}
        initial="hidden"
        animate="show"
      >
        World's First causal reasoning & verification engine for AI filmmaking.
      </motion.p>

      <motion.div
        className="rh-hero-ctas"
        variants={restItem}
        custom={0.6}
        initial="hidden"
        animate="show"
      >
        <SignUpButton variant="primary" />
        <TalkToFoundersButton variant="ghost" />
      </motion.div>

    </header>
  );
}
